import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CreditCard, Landmark, CheckCircle2, Clock, ShieldCheck, RefreshCw } from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, profileService, stripeConnectApi } from '../services/api';

// Standalone payout onboarding, reachable from any role via the account nav (not nested inside
// Bazaar's "My Bazaar > Sales" tab). The backend branches storage between CarrierProfiles and
// CustomerProfiles for /onboard and /status (see StripeConnectController.GetStripeAccountAsync),
// so Express onboarding works for any seller/carrier. /bank-account, however, only ever writes to
// CarrierProfiles - a non-carrier submitting it would get a 200 with the row silently not
// persisted - so the Bank tab is restricted to carriers here to match what the backend can do.

type Tab = 'express' | 'bank';

interface BankForm {
  fullName: string;
  sortCode: string;
  accountNumber: string;
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  addressLine1: string;
  city: string;
  postcode: string;
}

const EMPTY_BANK_FORM: BankForm = {
  fullName: '', sortCode: '', accountNumber: '',
  dobDay: '', dobMonth: '', dobYear: '',
  addressLine1: '', city: '', postcode: '',
};

export default function PayoutSetupPage() {
  const { getToken, account } = useAppAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('express');
  const [bankForm, setBankForm] = useState<BankForm>(EMPTY_BANK_FORM);
  const [bankError, setBankError] = useState<string | null>(null);

  const { data: profile } = useQuery({
    queryKey: ['profile', 'me'],
    queryFn: () => profileService(createApiClient(getToken)).getProfile(),
    enabled: !!account,
  });
  const isCarrier = profile?.role?.toLowerCase() === 'carrier';

  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['stripe-connect', 'status'],
    queryFn: async () => stripeConnectApi.getOnboardingStatus(await getToken()),
    enabled: !!account,
  });

  const onboardMutation = useMutation({
    mutationFn: async () => stripeConnectApi.startExpressOnboarding(await getToken()),
    onSuccess: (result) => { window.location.href = result.onboardingUrl; },
  });

  const refreshMutation = useMutation({
    mutationFn: async () => stripeConnectApi.getOnboardingStatus(await getToken()),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['stripe-connect', 'status'] }),
  });

  const bankMutation = useMutation({
    mutationFn: async () => stripeConnectApi.registerBankAccount(bankForm, await getToken()),
    onSuccess: () => {
      setBankError(null);
      qc.invalidateQueries({ queryKey: ['stripe-connect', 'status'] });
    },
    onError: (e: any) => setBankError(e?.response?.data?.error ?? e?.response?.data?.title ?? e?.message ?? 'Could not register bank account.'),
  });

  const setBankField = (key: keyof BankForm) => (value: string) => setBankForm(f => ({ ...f, [key]: value }));

  const bankFormValid = bankForm.fullName.trim()
    && bankForm.sortCode.trim().length === 6
    && bankForm.accountNumber.trim().length >= 8
    && bankForm.dobDay && bankForm.dobMonth && bankForm.dobYear
    && bankForm.addressLine1.trim() && bankForm.city.trim() && bankForm.postcode.trim();

  if (!account) return null;

  return (
    <div data-testid="payout-setup-screen" className="page-shell-tight">
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Payout Setup</h1>
        <p className="text-sm text-gray-500 mt-1">Choose how you want to receive earnings from TransitDeal — job payouts and Bazaar sales alike.</p>
      </div>

      <div className="flex bg-gray-100 rounded-xl p-1 mb-5 gap-1">
        <button
          type="button"
          data-testid="payout-setup-express-tab"
          onClick={() => setTab('express')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold ${tab === 'express' ? 'bg-white shadow-sm text-violet-700' : 'text-gray-500'}`}
        >
          <CreditCard className="w-3.5 h-3.5" /> Stripe Express
        </button>
        <button
          type="button"
          data-testid="payout-setup-bank-tab"
          onClick={() => setTab('bank')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-semibold ${tab === 'bank' ? 'bg-white shadow-sm text-violet-700' : 'text-gray-500'}`}
        >
          <Landmark className="w-3.5 h-3.5" /> Bank Details
        </button>
      </div>

      {tab === 'express' ? (
        <div className="space-y-4">
          <section className="card p-5">
            {statusLoading ? (
              <p className="text-sm text-gray-500">Checking Stripe status…</p>
            ) : (
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${status?.onboarded ? 'bg-green-100' : 'bg-amber-100'}`}>
                  {status?.onboarded
                    ? <CheckCircle2 className="w-5 h-5 text-green-600" />
                    : <Clock className="w-5 h-5 text-amber-600" />}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {status?.onboarded ? 'Stripe Connected' : status?.accountId ? 'Onboarding Incomplete' : 'Not Connected'}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {status?.onboarded
                      ? 'Payments can be transferred to your Stripe account.'
                      : status?.accountId
                        ? 'Continue onboarding to enable payouts.'
                        : 'Link your Stripe account to receive payments.'}
                  </p>
                </div>
              </div>
            )}

            {status?.accountId && (
              <div className="mt-3 pt-3 border-t border-gray-100 space-y-1">
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Account ID</span>
                  <span className="text-gray-700 font-mono truncate ml-3">{status.accountId}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Details submitted</span>
                  <span>{status.detailsSubmitted ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500">Charges enabled</span>
                  <span>{status.chargesEnabled ? 'Yes' : 'No'}</span>
                </div>
              </div>
            )}
          </section>

          {!status?.onboarded && (
            <button
              type="button"
              data-testid="payout-setup-start-button"
              onClick={() => onboardMutation.mutate()}
              disabled={onboardMutation.isPending}
              className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {onboardMutation.isPending
                ? 'Starting…'
                : status?.accountId ? 'Continue Onboarding' : 'Start Stripe Onboarding'}
            </button>
          )}

          {status?.accountId && !status.onboarded && (
            <button
              type="button"
              data-testid="payout-setup-refresh-button"
              onClick={() => refreshMutation.mutate()}
              disabled={refreshMutation.isPending}
              className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium text-violet-600 disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshMutation.isPending ? 'animate-spin' : ''}`} /> Check Status Again
            </button>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex gap-2">
            <ShieldCheck className="w-4 h-4 text-blue-700 shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              You'll be taken to Stripe's secure page to verify your identity and link a bank account.
              This is required for TransitDeal to transfer earnings directly to you.
            </p>
          </div>
        </div>
      ) : !isCarrier ? (
        <div data-testid="payout-setup-bank-restricted" className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-4 text-sm text-gray-600">
          Bank/Custom account setup is available for driver accounts. Use Stripe Express above to set up payouts for Bazaar sales.
        </div>
      ) : status?.onboarded ? (
        <div data-testid="payout-setup-bank-connected" className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">Bank account connected</p>
            <p className="text-xs text-green-700 mt-0.5">Your account is set up for payouts.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex gap-2">
            <ShieldCheck className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              By providing your details, you authorise TransitDeal to create a Stripe Custom account
              and store your banking information securely. We take on KYC responsibility.
            </p>
          </div>

          <section data-testid="payout-setup-bank-form" className="card p-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Personal Details</h2>
            <div className="space-y-3">
              <div>
                <label className="form-label">Full name</label>
                <input className="form-input" value={bankForm.fullName} onChange={e => setBankField('fullName')(e.target.value)} placeholder="Jane Smith" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="form-label">Day</label>
                  <input className="form-input" value={bankForm.dobDay} onChange={e => setBankField('dobDay')(e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="DD" maxLength={2} />
                </div>
                <div>
                  <label className="form-label">Month</label>
                  <input className="form-input" value={bankForm.dobMonth} onChange={e => setBankField('dobMonth')(e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="MM" maxLength={2} />
                </div>
                <div>
                  <label className="form-label">Year</label>
                  <input className="form-input" value={bankForm.dobYear} onChange={e => setBankField('dobYear')(e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="YYYY" maxLength={4} />
                </div>
              </div>
              <div>
                <label className="form-label">Address</label>
                <input className="form-input" value={bankForm.addressLine1} onChange={e => setBankField('addressLine1')(e.target.value)} placeholder="12 High Street" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="form-label">City</label>
                  <input className="form-input" value={bankForm.city} onChange={e => setBankField('city')(e.target.value)} placeholder="London" />
                </div>
                <div>
                  <label className="form-label">Postcode</label>
                  <input className="form-input" value={bankForm.postcode} onChange={e => setBankField('postcode')(e.target.value.toUpperCase())} placeholder="SW1A 1AA" />
                </div>
              </div>
            </div>
          </section>

          <section className="card p-5">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Bank Details</h2>
            <div className="space-y-3">
              <div>
                <label className="form-label">Sort code (6 digits, no dashes)</label>
                <input className="form-input" value={bankForm.sortCode} onChange={e => setBankField('sortCode')(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="123456" maxLength={6} />
              </div>
              <div>
                <label className="form-label">Account number</label>
                <input className="form-input" value={bankForm.accountNumber} onChange={e => setBankField('accountNumber')(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="12345678" maxLength={8} />
              </div>
            </div>
          </section>

          {bankError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs">{bankError}</div>
          )}

          <button
            type="button"
            data-testid="payout-setup-register-bank-button"
            onClick={() => bankMutation.mutate()}
            disabled={!bankFormValid || bankMutation.isPending}
            className="btn-primary w-full disabled:opacity-50"
          >
            {bankMutation.isPending ? 'Registering…' : 'Register Bank Account'}
          </button>
        </div>
      )}
    </div>
  );
}
